import {
  IManager,
  ClonedEventType,
  Qos,
  ResultTypeEnum,
  ILogHandler,
  IQosStartSchema,
  IQosEndSchema,
  Engagement,
  ISingleEvent,
  IEngagementSingleSchema
} from "@ms/telemetry-manager";
import { ITelemetryHandler } from "@ms/telemetry-manager/lib/ITelemetryHandler";
import {
  CaughtError,
  ICaughtErrorSingleSchema
} from "@ms/telemetry-manager/lib/events/CaughtError.event";
import {
  QosError,
  IQosErrorSingleSchema
} from "@ms/telemetry-manager/lib/events/QosError.event";
import { IClonedEvent } from "@ms/telemetry-manager/lib/IClonedEvent";
import { CorrelationVector } from "@ms/telemetry-manager/lib/CorrelationVector";
import { PiiKind } from "@msteams/core-services-telemetry-provider";
import {
  scenarios,
  FilesAnalyticsScenarios
} from "@msteams/bridges-models-files";
import {
  loggingPrefixFacet,
  itemCacheStoreFilesKeys,
  threadIdFacet
} from "@msteams/bridges-models-files-item-scope";
import { ITypedScenario, IEventData } from "@msteams/bridges-models-ts-logging";
import { IAnalyticsService } from "@msteams/bridges-models-ts-analytics";
import { LoggingService } from "@msteams/bridges-services-ts-logging";
import { matchAndScrubPii } from "@msteams/core-services-telemetry";
import { reverseLookupEnum } from "@msteams/services-utilities-common";
import {
  extractCorrelationId,
  scrubUnknownFileExtensions
} from "@msteams/bridges-services-utilities-files";

import { getFilesPrefetcher } from "../files-prefetcher";
import {
  itemsViewErrorPrefix,
  teamsTelemetryDelimiter,
  itemsViewTelemetryDelimiterRegex,
  errorCodes,
  eventNames,
  itemsViewUploadQos,
  tenMinutesInMilliseconds
} from "../constants";

type IErrorEventDataType = ICaughtErrorSingleSchema | IQosErrorSingleSchema;
type IStartEventDataType = IQosStartSchema;
type IEndEventDataType = IQosEndSchema;

export interface IDataBag {
  [key: string]: {} | string | number | boolean | null | undefined;
}

export class ItemsViewTelemetryHandler implements ITelemetryHandler {
  // Here weak map is used to avoid memory leaks for which we don't receive the corresponding end event
  private eventScenarioMap: WeakMap<
    CorrelationVector,
    ITypedScenario<IEventData>
  >;

  public constructor(
    private analyticsService: IAnalyticsService<FilesAnalyticsScenarios>,
    private createFolderAndRefreshSiteUrl?: (threadId: string) => void
  ) {
    this.eventScenarioMap = new WeakMap();
  }
  /**
   * Attaches the telemetry handler to a log manager.
   * When attach() is called, it *should* call `manager.addLogHandler()`, supplying a callback.
   * This function should return a function which, when invoked, removes any attached log handlers.
   * @param manager A log manager to which to attach this handler's log handlers.
   */
  public attach(manager: IManager): () => void {
    const logHandler = this.createLogHandler();
    manager.addLogHandler(logHandler);
    return this.getDisposeHandlerCallback(manager, logHandler);
  }

  private createLogHandler(): ILogHandler {
    return (event: IClonedEvent): void => {
      if (!event || !event.vector || !event.data) {
        return;
      }
      // List of single events:
      // CaughtError - is fired whenever items-view catch a script error
      // QosError - is fired to handle unexpected API results, by some pathways
      // Trace - is presently unused
      // Validation - can be fired if the AriaTelemetryHandler is configured to validate event input
      // Verbose - is used to report status and states from various places, for diagnostics.
      // Engagement - BI events
      if (this.isErrorEvent(event)) {
        this.handleErrorEvent(event);
        return;
      }
      if (Engagement.isTypeOf(event)) {
        this.handleEngagementEvent(event);
        return;
      }
      if (Qos.isTypeOf(event)) {
        this.handleQoSEvent(event);
      }
    };
  }

  private isErrorEvent(event: IClonedEvent): boolean {
    return (
      event.eventType === ClonedEventType.Single &&
      (QosError.isTypeOf(event) || CaughtError.isTypeOf(event))
    );
  }

  private handleErrorEvent(event: IClonedEvent<IErrorEventDataType>) {
    const { data: eventData } = event;
    let errorMessage = this.removePii(eventData.message);
    const eventName = (eventData as IQosErrorSingleSchema).name;
    if (eventName) {
      errorMessage = `${eventName} - ${errorMessage}`;
    }
    const resultCode = (eventData as IQosErrorSingleSchema).resultCode;
    LoggingService.logError(`${itemsViewErrorPrefix}${errorMessage}`);
    if (
      eventName === eventNames.getSPFolderItemSet &&
      resultCode &&
      (resultCode === errorCodes.NOTFOUND ||
        resultCode.includes(errorCodes.NOTFOUNDSPECIAL)) &&
      this.createFolderAndRefreshSiteUrl
    ) {
      const threadId =
        getFilesPrefetcher().readFromStore(
          threadIdFacet,
          itemCacheStoreFilesKeys.threadIdKey
        ) || "";
      this.createFolderAndRefreshSiteUrl(threadId);
    }
  }

  private handleEngagementEvent(
    event: ISingleEvent<IEngagementSingleSchema>
  ): void {
    const { context, data } = event;
    if (!data.name) {
      return;
    }
    const eventEnum: FilesAnalyticsScenarios | undefined = reverseLookupEnum(
      FilesAnalyticsScenarios,
      data.name
    );
    if (!eventEnum) {
      return;
    }

    // Can either be word, powerpoint, etc.
    const documentType = data.extraData && data.extraData.documentType;
    // Actual file extension
    const extension =
      data.extraData &&
      data.extraData.extension &&
      scrubUnknownFileExtensions(
        data.extraData.extension.toString().replace(".", "")
      );
    // For detecting a folder
    const objectTypeFolderField = data.objectType;
    // For detecting File/Folder. Folder can be marked in extraData.objectType also
    const objectTypeFolderOrFileField =
      data.extraData &&
      data.extraData.objectType &&
      data.extraData.objectType.toString().toLowerCase();

    const options: IDataBag = {
      fileAction: data.name,
      fileControl:
        context && context.component ? context.component : data.component,
      // The OR condition is for the order fileType should be checked in
      fileType:
        documentType ||
        extension ||
        objectTypeFolderField ||
        objectTypeFolderOrFileField,
      fileSource: this.getLoggingPrefix()
    };

    if (context && context.usageType) {
      // Add usage type in dataBag if a value is present
      options.usageType = context.usageType.toString().toLowerCase();
    }

    this.analyticsService.logPanelActionEvent(eventEnum, options);
  }

  private handleQoSEvent(event: IClonedEvent): void {
    if (event.eventType === ClonedEventType.Start) {
      this.handleStartEvent(event);
      return;
    }
    if (event.eventType === ClonedEventType.End) {
      this.handleEndEvent(event);
    }
  }

  private handleStartEvent(event: IClonedEvent<IStartEventDataType>): void {
    // Don't create a scenario if the event doesn't have a name or the event is already stored in map
    if (event.data.name && !this.eventScenarioMap.has(event.vector)) {
      const scenario = LoggingService.startNewScenario({
        scenarioName: this.getScenarioName(event.data.name),
        timeoutMs:
          event.data.name === itemsViewUploadQos.uploadFile ||
          event.data.name === itemsViewUploadQos.uploadBatch
            ? tenMinutesInMilliseconds // 10 minute timeout for upload event
            : undefined
      });
      this.eventScenarioMap.set(event.vector, scenario);
    }
  }

  private handleEndEvent(event: IClonedEvent<IEndEventDataType>): void {
    const { data: eventData, vector: correlationVector } = event;
    if (this.eventScenarioMap.has(correlationVector)) {
      const scenario = this.eventScenarioMap.get(correlationVector);
      this.eventScenarioMap.delete(correlationVector);
      const scenarioData: IEventData = {
        itemsViewData: {
          duration: event.endTime - event.startTime,
          context: event.context
        }
      };
      switch (eventData.resultType) {
        case ResultTypeEnum.Failure:
          scenarioData.itemsViewData.error = this.removePii(eventData.error);
          scenario!!!.fail(scenarioData);
          break;
        case ResultTypeEnum.ExpectedFailure:
          scenarioData.itemsViewData.error = this.removePii(eventData.error);
          scenarioData.itemsViewData.extraData = eventData.extraData;
          scenario!!!.stop(scenarioData);
          break;
        case ResultTypeEnum.Success:
          scenarioData.itemsViewData.extraData = eventData.extraData;
          scenario!!!.stop(scenarioData);
          break;
        default:
          scenarioData.itemsViewData.error = `Invalid result type: ${eventData.resultType}`;
          scenarioData.itemsViewData.extraData = eventData.extraData;
          scenario!!!.fail(scenarioData);
          break;
      }
    }
  }

  // If there is any correlation id in the message then just return that,
  // otherwise return the message after pii scrubbing
  private removePii(errorMsg: string | undefined) {
    return errorMsg
      ? extractCorrelationId(errorMsg) ||
          matchAndScrubPii(errorMsg, [
            PiiKind.Uri,
            PiiKind.SmtpAddress,
            PiiKind.IPV4Address,
            PiiKind.IPv6Address,
            PiiKind.PhoneNumber,
            PiiKind.FilePath
          ])
      : "Undefined error";
  }

  /**
   * ItemsView scenario sends events with "." as delimeter. Teams telemetry tracking only accepts "_" as delimeter.
   * This method gives a scenario name supported by teams telemetry.
   * For e.g. - name.someName.otherName will be formated to name_someName_otherName
   */
  private getScenarioName(eventName: string): string {
    const formattedScenarioName = eventName.replace(
      itemsViewTelemetryDelimiterRegex,
      teamsTelemetryDelimiter
    );
    return `${this.getLoggingPrefix()}${teamsTelemetryDelimiter}${formattedScenarioName}`;
  }

  private getLoggingPrefix() {
    const filePrefetcher = getFilesPrefetcher();
    // For CFT store is not available, so for now hard-coding the logging prefix
    // TODO - WI 449689: Once creation of store completed for CFT, remove this hard-coded value
    const loggingPrefix = filePrefetcher.getStore()
      ? filePrefetcher.readFromStore(
          loggingPrefixFacet,
          itemCacheStoreFilesKeys.loggingPrefixKey
        ) || scenarios.prefetchFilesPrefix
      : scenarios.channelFilesPrefix;
    return loggingPrefix;
  }

  /**
   * Return a function to *dispose* the handler from manager
   */
  private getDisposeHandlerCallback(
    manager: IManager,
    logHandler: ILogHandler
  ): () => void {
    return () => {
      if (logHandler) {
        manager.removeLogHandler(logHandler);
      }
    };
  }
}
